import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FaSave, FaExclamationTriangle } from 'react-icons/fa';

const CoachPrivileges = ({ coach, onSave, loading, onChanges }) => {
    const initialPrivileges = useRef(null);
    const [privileges, setPrivileges] = useState({
        groups: { view: false, write: false },
        events: { view: false, write: false },
        coaches: { view: false, write: false },
        students: { view: false, write: false },
        trainingPlan: { view: false, write: false }
    });
    const [hasChanges, setHasChanges] = useState(false);
    const [showAlert, setShowAlert] = useState(false);

    // Initialize privileges and store initial state
    useEffect(() => {
        if (coach?.accessPrivilege) {
            const initial = {
                groups: { ...coach.accessPrivilege.groups },
                events: { ...coach.accessPrivilege.events },
                coaches: { ...coach.accessPrivilege.coaches },
                students: { ...coach.accessPrivilege.students },
                trainingPlan: { ...coach.accessPrivilege.trainingPlan }
            };
            setPrivileges(initial);
            initialPrivileges.current = initial;
        }
    }, [coach]);

    const handlePrivilegeChange = (section, type, value) => {
        const newPrivileges = {
            ...privileges,
            [section]: {
                ...privileges[section],
                [type]: value
            }
        };
        setPrivileges(newPrivileges);

        // Compare with initial state
        const hasActualChanges = !isEqual(newPrivileges, initialPrivileges.current);
        setHasChanges(hasActualChanges);
        if (onChanges) onChanges(hasActualChanges);
    };

    const handleSave = async () => {
        try {
            await onSave(privileges);
            // Update initial state after successful save
            initialPrivileges.current = {
                groups: { ...privileges.groups },
                events: { ...privileges.events },
                coaches: { ...privileges.coaches },
                students: { ...privileges.students },
                trainingPlan: { ...privileges.trainingPlan }
            };
            setHasChanges(false);
            setShowAlert(false);
            if (onChanges) onChanges(false);
        } catch (error) {
            console.error('Error saving privileges:', error);
        }
    };

    // Deep equality comparison function
    const isEqual = (obj1, obj2) => {
        if (obj1 === obj2) return true;
        if (!obj1 || !obj2) return false;

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) return false;

        return keys1.every(key => {
            const val1 = obj1[key];
            const val2 = obj2[key];

            if (typeof val1 === 'object' && val1 !== null) {
                return isEqual(val1, val2);
            }

            return val1 === val2;
        });
    };

    const sections = [
        { id: 'groups', label: 'קבוצות' },
        { id: 'events', label: 'אירועים' },
        { id: 'coaches', label: 'מאמנים' },
        { id: 'students', label: 'ספורטאים' },
        { id: 'trainingPlan', label: 'תוכנית האימונים' }
    ];

    return (
        <Card className="mt-3">
            <Card.Header className="bg-light">
                <div className="d-flex" style={{ justifyContent: "space-between", alignItems: "center" }}>
                    <h5 className="mb-0 fw-bold" style={{ flex: "1" }}>הרשאות מאמן</h5>
                    <Button
                        variant={hasChanges ? "primary" : "outline-secondary"}
                        size="sm"
                        onClick={handleSave}
                        disabled={!hasChanges || loading}
                        style={{ width: "fit-content" }}
                    >
                        {loading ? (
                            <div style={{ width: "fit-content", flex: "1" }}>
                                <Spinner animation="border" size="sm" className="me-2" style={{ marginLeft: "10px" }} />
                                שומר...
                            </div>
                        ) : (
                            <div style={{ width: "fit-content", flex: "1" }}>
                                <FaSave className="me-2" style={{ marginLeft: "10px" }} />
                                שמור שינויים
                            </div>
                        )}
                    </Button>
                </div>
            </Card.Header>
            <Card.Body className="p-3">
                {showAlert && (
                    <Alert variant="warning" onClose={() => setShowAlert(false)} dismissible className="mb-2">
                        <FaExclamationTriangle className="me-2" />
                        יש לך שינויים שלא נשמרו. האם ברצונך לשמור?
                    </Alert>
                )}

                <div className="d-flex flex-column gap-2">
                    {sections.map(section => (
                        <div key={section.id} className="d-flex justify-content-between align-items-center border-bottom pb-2">
                            <span className="fw-medium">{section.label}</span>
                            <div className="d-flex gap-3">
                                <Form.Check
                                    type="switch"
                                    id={`${section.id}-view`}
                                    label="צפייה"
                                    checked={privileges[section.id]?.view}
                                    onChange={(e) => handlePrivilegeChange(section.id, 'view', e.target.checked)}
                                />
                                <Form.Check
                                    type="switch"
                                    id={`${section.id}-write`}
                                    label="עריכה"
                                    checked={privileges[section.id]?.write}
                                    onChange={(e) => handlePrivilegeChange(section.id, 'write', e.target.checked)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </Card.Body>
        </Card>
    );
};

export default CoachPrivileges; 